import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef } from "react";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import { File, Folder } from "src/app/models/screens/files.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import CloudieScreenViewModel, {
  ACCEPTED_MEDIA_TYPES,
} from "src/app/screens/cloudie/cloudie.viewmodel";
import cs from "src/app/styles/screens/cloudie.style";

const CloudieScreenView: React.FC = () => {
  const vm = CloudieScreenViewModel();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasContent = vm.folders.length > 0 || vm.files.length > 0;

  return (
    <Box sx={cs.page}>
      <DrawerComponentView selectedId="Cloudie" />
      <DrawerAwareContainerComponentView drawerWidth={250} sx={cs.container}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_MEDIA_TYPES}
          style={{ display: "none" }}
          onChange={(e) => vm.onFilesSelected(e.target.files)}
          onClick={(e) => ((e.target as HTMLInputElement).value = "")}
        />
        {vm.userRole === "Parent" && (
          <Grid container sx={{ justifyContent: "flex-end", mb: 2 }}>
            <Grid size={{ xl: 4, xs: 10 }}>
              <Paper elevation={0} sx={cs.selectorPaper}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cloudie-owner-label">Viewing as</InputLabel>
                  <Select
                    labelId="cloudie-owner-label"
                    value={vm.selectedOwnerId}
                    label="Viewing as"
                    onChange={(e: SelectChangeEvent) =>
                      vm.onOwnerChanged(e.target.value)
                    }
                    disabled={vm.isLoadingChildren}
                  >
                    <MenuItem value="you">You</MenuItem>
                    {vm.children.map((child) => (
                      <MenuItem key={child._id} value={child._id}>
                        {child.firstName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        )}
        {vm.userRole === "Admin" && (
          <Grid container sx={{ justifyContent: "flex-end", mb: 2 }}>
            <Grid size={{ xl: 4, xs: 10 }}>
              <Paper elevation={0} sx={cs.selectorPaper}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cloudie-admin-label">
                    Viewing Cloudie of
                  </InputLabel>
                  <Select
                    labelId="cloudie-admin-label"
                    value={vm.selectedOwnerId}
                    label="Viewing Cloudie of"
                    onChange={(e: SelectChangeEvent) =>
                      vm.onOwnerChanged(e.target.value)
                    }
                    disabled={vm.isLoadingAdminUsers}
                  >
                    <MenuItem value="you">You</MenuItem>
                    {vm.adminUsers.map((u: RequestManagementUser) => (
                      <MenuItem key={u.id} value={u.id}>
                        {`${u.firstName} ${u.lastName}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        )}
        <Box sx={cs.headerRow}>
          <Typography sx={cs.titleText}>Cloudie</Typography>

          <Box sx={cs.headerActions}>
            {vm.storageInfo && (
              <Box sx={cs.storagePill}>
                <StorageIcon sx={{ fontSize: 15, opacity: 0.5 }} />
                <Box sx={cs.storagePillTrack}>
                  <Box
                    sx={{
                      width: `${vm.storageUsedPct}%`,
                      height: "100%",
                      borderRadius: 99,
                      backgroundColor:
                        vm.storageUsedPct > 85
                          ? "#f44336"
                          : vm.storageUsedPct > 65
                            ? "#ff9800"
                            : "#1976d2",
                      transition: "width 0.4s",
                    }}
                  />
                </Box>
                <Typography
                  sx={{ fontSize: 11, whiteSpace: "nowrap", opacity: 0.7 }}
                >
                  {vm.formatBytes(vm.storageInfo.usedBytes)} /{" "}
                  {vm.formatBytes(vm.storageInfo.maxBytes)}
                </Typography>
                {!vm.isViewingAsAdmin && (
                  <Tooltip title="Request more storage">
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      onClick={vm.openStorageRequestDialog}
                    >
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            {!vm.isViewingAsAdmin ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  onClick={vm.openCreateFolderDialog}
                  sx={{ borderRadius: 2 }}
                >
                  New Folder
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ borderRadius: 2 }}
                >
                  Add Media
                </Button>
              </>
            ) : (
              <Chip
                label="View only — Admin mode"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        {vm.breadcrumbs.length > 1 && (
          <Box sx={cs.breadcrumbRow}>
            {vm.breadcrumbs.map((crumb, idx) => {
              const isLast = idx === vm.breadcrumbs.length - 1;
              return (
                <Box
                  key={crumb.id ?? "root"}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {idx > 0 && (
                    <ChevronRightIcon
                      sx={{ fontSize: 14, opacity: 0.3, mx: 0.25 }}
                    />
                  )}
                  <Typography
                    onClick={
                      !isLast ? () => vm.onBreadcrumbClicked(crumb) : undefined
                    }
                    sx={isLast ? cs.breadcrumbActive : cs.breadcrumbItem}
                  >
                    {crumb.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        {vm.isLoadingItems ? (
          <Box sx={cs.loadingBox}>
            <CircularProgress size={36} />
          </Box>
        ) : !hasContent ? (
          <Box sx={cs.emptyState}>
            <PermMediaOutlinedIcon sx={cs.emptyIcon} />
            <Typography sx={cs.emptyText}>No media here yet</Typography>
            {!vm.isViewingAsAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddPhotoAlternateOutlinedIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ borderRadius: 2, mt: 0.5 }}
              >
                Add your first photo or video
              </Button>
            )}
          </Box>
        ) : (
          <>
            {vm.folders.length > 0 && (
              <Box sx={cs.folderSection}>
                <Typography sx={cs.folderSectionLabel}>Albums</Typography>
                <Grid container spacing={1.5}>
                  {vm.folders.map((folder: Folder) => (
                    <Grid
                      key={folder.id}
                      size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                    >
                      <Box
                        className="folderCard"
                        sx={cs.folderCard}
                        onDoubleClick={() => vm.onFolderOpen(folder)}
                        onClick={() => vm.onFolderOpen(folder)}
                      >
                        <Box sx={cs.folderIconWrap}>
                          <FolderIcon sx={{ color: "#f9a825", fontSize: 26 }} />
                        </Box>
                        <Typography
                          sx={{
                            ...cs.folderName,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                          title={folder.name}
                        >
                          {folder.name}
                        </Typography>
                        {folder.accessList.length > 0 && (
                          <Tooltip
                            title={`Shared with ${folder.accessList.length} user(s)`}
                          >
                            <PeopleAltOutlinedIcon
                              sx={{ fontSize: 15, opacity: 0.4 }}
                            />
                          </Tooltip>
                        )}
                        {!vm.isViewingAsAdmin && (
                          <Box
                            className="folderActions"
                            sx={cs.folderActions}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="Manage access">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  vm.openAccessDialog(folder, "folder")
                                }
                              >
                                <LockOpenIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  vm.openRenameDialog(
                                    folder.id,
                                    "folder",
                                    folder.name,
                                  )
                                }
                              >
                                <DriveFileRenameOutlineIcon
                                  sx={{ fontSize: 16 }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  vm.openDeleteDialog(
                                    folder.id,
                                    "folder",
                                    folder.name,
                                  )
                                }
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            {vm.files.length > 0 && (
              <Box sx={cs.mediaSection}>
                {vm.folders.length > 0 && (
                  <Typography sx={cs.mediaSectionLabel}>
                    Photos & Videos
                  </Typography>
                )}
                <Grid container spacing={1}>
                  {vm.files.map((file: File, idx: number) => {
                    const isVideo = file.mimeType.startsWith("video/");
                    return (
                      <Grid key={file.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                        <Box
                          sx={cs.mediaCard}
                          onClick={() => vm.openViewer(file, idx)}
                        >
                          <Box sx={cs.mediaAspectBox}>
                            <Box
                              component="img"
                              src={vm.mediaUrl(file, true)}
                              alt={file.name}
                              className="mediaCardImg"
                              sx={cs.mediaImg}
                              loading="lazy"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.opacity =
                                  "0.1";
                              }}
                            />
                            {isVideo && (
                              <PlayCircleIcon sx={cs.videoPlayBadge} />
                            )}
                            {file.accessList.length > 0 && (
                              <Box sx={cs.sharedBadge}>
                                <PeopleAltOutlinedIcon
                                  sx={{ fontSize: 12, color: "#fff" }}
                                />
                              </Box>
                            )}
                            <Box className="mediaOverlay" sx={cs.mediaOverlay}>
                              <Typography
                                sx={{
                                  ...cs.mediaOverlayName,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {file.name}
                              </Typography>
                              <Box
                                sx={cs.mediaOverlayActions}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    sx={cs.mediaOverlayIconBtn}
                                    onClick={() => vm.onDownloadFile(file)}
                                  >
                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                {!vm.isViewingAsAdmin && (
                                  <>
                                    <Tooltip title="Manage access">
                                      <IconButton
                                        size="small"
                                        sx={cs.mediaOverlayIconBtn}
                                        onClick={() =>
                                          vm.openAccessDialog(file, "file")
                                        }
                                      >
                                        <LockOpenIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Rename">
                                      <IconButton
                                        size="small"
                                        sx={cs.mediaOverlayIconBtn}
                                        onClick={() =>
                                          vm.openRenameDialog(
                                            file.id,
                                            "file",
                                            file.name,
                                          )
                                        }
                                      >
                                        <DriveFileRenameOutlineIcon
                                          sx={{ fontSize: 16 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        sx={{
                                          ...cs.mediaOverlayIconBtn,
                                          color: "#ff8a80",
                                        }}
                                        onClick={() =>
                                          vm.openDeleteDialog(
                                            file.id,
                                            "file",
                                            file.name,
                                          )
                                        }
                                      >
                                        <DeleteOutlineIcon
                                          sx={{ fontSize: 16 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              mt: 0.5,
                              px: 0.5,
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </>
        )}
      </DrawerAwareContainerComponentView>
      {vm.viewerFile && (
        <Box sx={cs.lightboxBackdrop} onClick={vm.closeViewer}>
          <Box sx={cs.lightboxTopBar} onClick={(e) => e.stopPropagation()}>
            <Typography sx={cs.lightboxFileName}>
              {vm.viewerFile.name}
            </Typography>
            <Box sx={cs.lightboxTopActions}>
              <Tooltip title="Download">
                <IconButton
                  sx={cs.lightboxIconBtn}
                  onClick={() => vm.onDownloadFile(vm.viewerFile!)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton sx={cs.lightboxIconBtn} onClick={vm.closeViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {vm.files.length > 1 && (
            <IconButton
              sx={{ ...cs.lightboxNavBtn, ...cs.lightboxPrevBtn }}
              onClick={(e) => {
                e.stopPropagation();
                vm.viewerPrev();
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {vm.viewerFile.mimeType.startsWith("image/") ? (
            <Box
              component="img"
              src={vm.mediaUrl(vm.viewerFile, false)}
              alt={vm.viewerFile.name}
              sx={cs.lightboxImg}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Box
              component="video"
              src={vm.mediaUrl(vm.viewerFile, false)}
              controls
              autoPlay
              sx={cs.lightboxVideo}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {vm.files.length > 1 && (
            <IconButton
              sx={{ ...cs.lightboxNavBtn, ...cs.lightboxNextBtn }}
              onClick={(e) => {
                e.stopPropagation();
                vm.viewerNext();
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
          {vm.files.length > 1 && (
            <Typography sx={cs.lightboxCounter}>
              {vm.viewerIndex + 1} / {vm.files.length}
            </Typography>
          )}
        </Box>
      )}
      {vm.uploadTasks.length > 0 && (
        <Box sx={cs.uploadPanel}>
          {vm.uploadTasks.map((task) => (
            <Paper key={task.id} elevation={0} sx={cs.uploadCard}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography
                  sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}
                  noWrap
                >
                  {task.fileName}
                </Typography>
                {(task.done || task.error) && (
                  <IconButton
                    size="small"
                    onClick={() => vm.dismissUploadTask(task.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {task.error ? (
                <Typography sx={{ fontSize: 12, color: "#f44336" }}>
                  {task.error}
                </Typography>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={task.progress}
                  color={task.done ? "success" : "primary"}
                />
              )}
              {task.done && !task.error && (
                <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 0.5 }}>
                  Upload complete
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}
      <Dialog
        open={vm.isCreateFolderOpen}
        onClose={vm.closeCreateFolderDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>New Album</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Album name"
            value={vm.newFolderName}
            onChange={(e) => vm.setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") vm.onCreateFolderConfirmed();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={cs.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={cs.modalCancelButton}
              onClick={vm.closeCreateFolderDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onCreateFolderConfirmed}
              loading={vm.isCreatingFolder}
            >
              Create
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.renameTarget}
        onClose={vm.closeRenameDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Rename{" "}
          {vm.renameTarget?.type === "folder" ? "Album" : "Photo / Video"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="New name"
            value={vm.renameName}
            onChange={(e) => vm.setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") vm.onRenameConfirmed();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={cs.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={cs.modalCancelButton}
              onClick={vm.closeRenameDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onRenameConfirmed}
              loading={vm.isRenaming}
            >
              Rename
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.deleteTarget}
        onClose={vm.closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Permanently?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{vm.deleteTarget?.name}</strong> will be permanently
            deleted.
            {vm.deleteTarget?.type === "folder" &&
              " All media inside this album will also be deleted."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box sx={cs.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={cs.modalCancelButton}
              onClick={vm.closeDeleteDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={vm.onDeleteConfirmed}
              loading={vm.isDeleting}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!vm.accessTarget}
        onClose={vm.closeAccessDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Share — {vm.accessTarget?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            People with access
          </Typography>
          {vm.accessTarget?.accessList.length === 0 && (
            <Typography sx={{ fontSize: 13, opacity: 0.6, mb: 2 }}>
              Not shared with anyone yet.
            </Typography>
          )}
          {vm.accessTarget?.accessList.map((entry) => (
            <Box key={entry.userId} sx={cs.accessListItem}>
              <Typography sx={{ fontSize: 14 }}>
                {entry.firstName} {entry.lastName}
              </Typography>
              <IconButton
                size="small"
                color="error"
                disabled={vm.isModifyingAccess}
                onClick={() => vm.onRemoveAccess(entry.userId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Add people</Typography>
          <TextField
            fullWidth
            size="small"
            label="Search users"
            value={vm.accessUserSearchText}
            onChange={(e) => vm.setAccessUserSearchText(e.target.value)}
            sx={{ mb: 1 }}
          />
          {vm.isSearchingAccessUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense sx={{ p: 0 }}>
              {vm.accessSearchResults
                .filter(
                  (u: RequestManagementUser) =>
                    !vm.accessTarget?.accessList.some((a) => a.userId === u.id),
                )
                .map((u: RequestManagementUser) => (
                  <ListItemButton
                    key={u.id}
                    onClick={() => vm.onAddAccess(u)}
                    disabled={vm.isModifyingAccess}
                  >
                    <ListItemText primary={`${u.firstName} ${u.lastName}`} />
                    <LockOpenIcon fontSize="small" sx={{ opacity: 0.4 }} />
                  </ListItemButton>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ px: 2, pb: 1.5, width: "100%" }}>
            <Button variant="outlined" fullWidth onClick={vm.closeAccessDialog}>
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vm.isStorageRequestOpen}
        onClose={vm.closeStorageRequestDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Request More Storage</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            How many extra gigabytes do you need? An admin will review your
            request in Cloudie.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Additional storage (GB)"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={vm.storageRequestGb}
            onChange={(e) => vm.setStorageRequestGb(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={cs.modalActionsContainer}>
            <Button
              variant="outlined"
              sx={cs.modalCancelButton}
              onClick={vm.closeStorageRequestDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={vm.onStorageRequestConfirmed}
              loading={vm.isSubmittingStorageRequest}
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={vm.isSnackbarError}
        isOpen={vm.isDisplayingSnackbar}
        message={vm.snackbarMessage}
        onClose={() => vm.setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default CloudieScreenView;
